import AppFadeView from "@toothfairy/shared-ui/AppFadeView";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppMenu from "@toothfairy/shared-ui/AppMenu";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { isLargeScreen } from "../../../MainViewContainer";
import QaSettingsAIView from "./SubComponents/QaSettingsAIView";
import Agents from "../../../../API/Agents";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../../../Router";

const QaSettingsAI = forwardRef(
  (
    {
      sectionTitle,
      initialMapValues,
      settingType,
      isEdit,
      opt,
      setDisplayedView,
      backTo,
      onMappingCreated,
      selectedId,
      handleGenerationItemSave,
      selectedIndex,
      setShowQaRoleModal,
      displayedRoles,
      availableFunctions,
      availableFunctionsLoaded,
      customCodeExecutionEnvironmentsLoaded,
      isChatBot,
      isBusinessAnalyst,
      isContentCreator,
      customCodeExecutionEnvironments,
      agentsChannels,
      setSelectedIndex,
      clearMemory,
      isAsyncProcessInProgress,
      saveVersion,
      isAIGenerated,
      isBuilderLayout,
      builderActiveSection,
      onBuilderContextChange,
    },
    ref,
  ) => {
    const AppToasty = AppToast.useToast();
    const history = useHistory();
    const { t } = useTranslation();
    const { getActiveWorkspace } = useWorkspaces();
    const { state, dispatch } = AppStateManager.useAppStateManager();
    const validationSchema = AppForms.yup.object().shape({
      // allow for no more than 4 words in sourceLanguage and targetLanguage
      label: AppForms.yup
        .string()
        .required("The agent name is required")
        .label()
        .max(50),
      interpolationString: AppForms.yup.string().label().max(64000).nullable(),
      customToolingInstructions: AppForms.yup
        .string()
        .label()
        .max(16000)
        .nullable(),
      dynamicModelSelectionInstructions: AppForms.yup
        .string()
        .label()
        .max(4096)
        .nullable(),
      description: AppForms.yup.string().label().max(255).nullable(),
      temperature: AppForms.yup.number().required().min(0.0).max(1).label(),
      qaUrl: AppForms.yup.string().label().nullable(),
      subject: AppForms.yup.string().label().max(50).nullable(),
      internetSearchLocation: AppForms.yup.string().label().max(100).nullable(),
      goals: AppForms.yup.string().required().label().max(16000),
      pertinencePassage: AppForms.yup.string().label().max(512).nullable(),
      inhibitionPassage: AppForms.yup.string().label().max(512).nullable(),
      defaultAnswer: AppForms.yup.string().label().max(255).nullable(),
      noKnowledgeDefaultAnswer: AppForms.yup
        .string()
        .label()
        .max(255)
        .nullable(),
      showAgentName: AppForms.yup.bool().label().nullable(),
      showWidgetControls: AppForms.yup.bool().label().nullable(),
      showToolCallsDetailsInWidget: AppForms.yup.bool().label().nullable(),
      hideReasoning: AppForms.yup.bool().label().nullable(),
      forcedTheme: AppForms.yup.string().nullable(),
      maxTokens: AppForms.yup.number().required().label().min(1),
      maxTopics: AppForms.yup.number().label().min(1).max(5),
      charting: AppForms.yup.bool().label().nullable(),
      icon: AppForms.yup.string().label().nullable(),
      largeLogo: AppForms.yup.string().label().nullable(),
      voiceFileKey: AppForms.yup.string().label().nullable(),
      icoUrl: AppForms.yup
        .string()
        .label()
        .nullable()
        .test(
          "is-domain",
          "Must be a valid url (e.g. https://mywebsite.com) with a .ico or .png extension",
          (value) => {
            if (!value) return true;
            const regex = /^https:\/\/(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9](?:\/[\w-]+)*\.(ico|png)$/i;
            return regex.test(value);
          },
        ),
      blender: AppForms.yup.bool().label().nullable(),
      hasTopicsContext: AppForms.yup.bool().label().nullable(),
      allowFeedback: AppForms.yup.bool().label().nullable(),
      promptBuffering: AppForms.yup.bool().label().nullable(),
      agentsPool: AppForms.yup.array().max(10).label().nullable(),
      agentDesktopCredentials: AppForms.yup.array().max(10).label().nullable(),
      agentDesktopBrowser: AppForms.yup.bool().label().nullable(),
      allowExternalAPI: AppForms.yup.bool().label().nullable(),
      reRank: AppForms.yup.bool().label().nullable(),
      maxPlanningSteps: AppForms.yup.number().label().min(1).max(10).nullable(),
      maxBrowserSteps: AppForms.yup.number().label().min(1).max(300).nullable(),
      plannerExecutionAttempts: AppForms.yup
        .number()
        .label()
        .min(1)
        .max(5)
        .nullable(),
      planningInstructions: AppForms.yup.string().label().max(16000).nullable(),
      reviewInstructions: AppForms.yup.string().label().max(16000).nullable(),
      ragInstructions: AppForms.yup.string().label().max(16000).nullable(),
      maxTotalToolCalls: AppForms.yup
        .number()
        .label()
        .min(1)
        .max(50000)
        .nullable(),
      maxConsecutiveSameTool: AppForms.yup
        .number()
        .label()
        .min(1)
        .max(300)
        .nullable(),
      maxConsecutiveFailures: AppForms.yup
        .number()
        .label()
        .min(1)
        .max(300)
        .nullable(),
      moderationMessage: AppForms.yup.string().label().max(512).nullable(),
      preventWidgetUsage: AppForms.yup.bool().label().nullable(),
      plannerRequiresApprovalForPlanExecution: AppForms.yup
        .bool()
        .label()
        .nullable(),
      plannerEmailOnApprovalRequest: AppForms.yup.bool().label().nullable(),
      plannerEmailOnFailedPlan: AppForms.yup.bool().label().nullable(),
      plannerEmailOnCompletedPlan: AppForms.yup.bool().label().nullable(),
      plannerAutoAgentSpawn: AppForms.yup.bool().label().nullable(),
      plannerDeepThinking: AppForms.yup.bool().label().nullable(),
      plannerRetainsSpawnedAgents: AppForms.yup.bool().label().nullable(),
      allowReplanning: AppForms.yup.bool().label().nullable(),
      disclaimer: AppForms.yup.string().label().nullable().max(255),
      quickQuestions: AppForms.yup
        .string()
        .label()
        .nullable()
        .max(255)
        .test(
          "no-more-than-2-semicolon",
          "No more than 3 initial questions allowed",
          (value) => {
            if (value) {
              const semicolonCount = value.split(";").length - 1;
              return semicolonCount <= 2;
            }
            return true;
          },
        ),
      color: AppForms.yup
        .string()
        .matches(
          /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/,
          "Must be a valid hex color",
        )
        .nullable(),
      darkColor: AppForms.yup
        .string()
        .matches(
          /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/,
          "Must be a valid hex color",
        )
        .nullable(),
      logoBackground: AppForms.yup
        .string()
        .matches(
          /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/,
          "Must be a valid hex color",
        )
        .nullable(),
      darkLogoBackground: AppForms.yup
        .string()
        .matches(
          /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/,
          "Must be a valid hex color",
        )
        .nullable(),
      placeholderInputMessage: AppForms.yup
        .string()
        .label()
        .nullable()
        .max(200),
      placeholderMessageOnLoading: AppForms.yup
        .string()
        .label()
        .nullable()
        .max(50),
      messageOnLaunch: AppForms.yup.string().label().nullable().max(255),
      // add allowed topics as array of strings with a max of 5 topics
      allowedTopics: AppForms.yup.array().label().max(10).nullable(),
      staticDocs: AppForms.yup.array().label().max(10).nullable(),
      ragSearchStrategy: AppForms.yup.string().label().nullable(),
      functionContext: AppForms.yup.array().label().max(10).nullable(),
      mode: AppForms.yup.string().required().label(),
      topK: AppForms.yup.number().required().label().min(1).max(50),
      summarisation: AppForms.yup.bool().label(),
      compressor: AppForms.yup.bool().label().nullable(),
      docTopK: AppForms.yup.number().label().required().min(1).max(50),
      recencyImportance: AppForms.yup.number().label().nullable().min(0).max(5),
      maxRecencyIncrease: AppForms.yup
        .number()
        .label()
        .nullable()
        .min(0)
        .max(1),
      recencyTopKIncreaseFactor: AppForms.yup
        .number()
        .label()
        .nullable()
        .min(0)
        .max(5),
      maxHistory: AppForms.yup.number().label().required().min(1).max(50),
      isLongTermMemoryEnabled: AppForms.yup.bool().label().nullable(),
      agentLongTermMemory: AppForms.yup.string().label().max(64000).nullable(),
      hasModeration: AppForms.yup.bool().label().nullable(),
      generateExtendedReport: AppForms.yup.bool().label().nullable(),
      minRetrievalScore: AppForms.yup
        .number()
        .label()
        .nullable()
        .min(0.01)
        .max(0.99),
      topicEnhancer: AppForms.yup.bool().label(),
      hasFunctions: AppForms.yup.bool().label().nullable(),
      hasImages: AppForms.yup.bool().label().nullable(),
      hasCode: AppForms.yup.bool().label().nullable(),
      allowImagesUpload: AppForms.yup.bool().label().nullable(),
      tabulate: AppForms.yup.bool().label().nullable(),
      allowAudiosUpload: AppForms.yup.bool().label().nullable(),
      allowDocsUpload: AppForms.yup.bool().label().nullable(),
      allowInternetSearch: AppForms.yup.bool().label().nullable(),
      allowAskQuestion: AppForms.yup.bool().label().nullable(),
      allowImagesGeneration: AppForms.yup.bool().label().nullable(),
      allowVideosGeneration: AppForms.yup.bool().label().nullable(),
      allow3dModelGenerations: AppForms.yup.bool().label().nullable(),
      advancedImageGeneration: AppForms.yup.bool().label().nullable(),
      allowDeepInternetSearch: AppForms.yup.bool().label().nullable(),
      internetSearchModes: AppForms.yup.array().label().nullable(),
      bannedDomains: AppForms.yup.array().label().nullable(),
      maxUrlsForInternetSearch: AppForms.yup
        .number()
        .label()
        .nullable()
        .min(1)
        .max(
          getActiveWorkspace()?.isExtendedBusinessAnalystReportEnabled
            ? 1000
            : 100,
        ),
      minDeepSearchPagesPercentage: AppForms.yup
        .number()
        .label()
        .nullable()
        .min(0.01)
        .max(1),
      channelCallbackDelay: AppForms.yup
        .number()
        .label()
        .nullable()
        .min(0)
        .max(120),
      hasNER: AppForms.yup.bool().label().nullable(),
      showReferencesToInternalDocs: AppForms.yup.bool().label().nullable(),
      showCitations: AppForms.yup.bool().label().nullable(),
      agenticRAG: AppForms.yup.bool().label().nullable(),
      useInterleavedReasoning: AppForms.yup.bool().label().nullable(),
      interleavedPreset: AppForms.yup.string().label().nullable(),
      llmSeed: AppForms.yup
        .number()
        .nullable()
        .integer()
        .min(0)
        .max(2147483647)
        .label(),
      advancedLanguageDetection: AppForms.yup.bool().label().nullable(),
      agentFunctions: AppForms.yup.array().label().nullable(),
      keyWordsForKnowledgeBase: AppForms.yup.bool().label().nullable(),
      plainTextOutput: AppForms.yup.bool().label().nullable(),
      extendedOutput: AppForms.yup.bool().label().nullable(),
      showTimeForResponse: AppForms.yup.bool().label().nullable(),
      dynamicPlaceholderMessageOnLoading: AppForms.yup
        .bool()
        .label()
        .nullable()
        .default(true),
      showDetectedLanguage: AppForms.yup.bool().label().nullable(),
      showRoutedModel: AppForms.yup.bool().label().nullable(),
      isMultiLanguageEnabled: AppForms.yup.bool().label().nullable(),
      restrictedAccess: AppForms.yup.bool().label().nullable(),
      restrictedAccessUsers: AppForms.yup.array().label().nullable(),
      enhancedApproverUsers: AppForms.yup.array().label().nullable(),
      showSplashWhenEmpty: AppForms.yup.bool().label().nullable(),
      splashTitle: AppForms.yup.string().label().nullable().max(50),
      allowAudiosGeneration: AppForms.yup.bool().label().nullable(),
      audiosGenerationTtsModel: AppForms.yup.string().label().nullable(),
      audiosGenerationVoiceName: AppForms.yup.string().label().nullable(),
      audiosGenerationSpeechMode: AppForms.yup.string().label().nullable(),
      chainOfThoughts: AppForms.yup.bool().label().nullable(),
      allowEmailToAgent: AppForms.yup.bool().label().nullable(),
      forceNewThreadOnProgrammaticRequest: AppForms.yup
        .bool()
        .label()
        .nullable(),

      allowedEmails: AppForms.yup.string().label().nullable(),
      contextRelevancyRatio: AppForms.yup
        .number()
        .nullable()
        .label()
        .min(0.01)
        .max(1),
      reasoningBudget: AppForms.yup
        .number()
        .nullable()
        .label()
        .min(1)
        .max(36000),
      llmProvider: AppForms.yup.string().label().nullable(),
      llmBaseModel: AppForms.yup.string().label().nullable(),
      agenticCodingModel: AppForms.yup.string().label().nullable(),
      functionCallingProvider: AppForms.yup.string().label().nullable(),
      functionCallingModel: AppForms.yup.string().label().nullable(),
      visionModel: AppForms.yup.string().label().nullable(),
      browserModel: AppForms.yup.string().label().nullable(),
      // Voice Agent fields
      voiceName: AppForms.yup.string().label().nullable(),
      sttModel: AppForms.yup.string().label().nullable(),
      llmVoiceModel: AppForms.yup.string().label().nullable(),
      ttsModel: AppForms.yup.string().label().nullable(),
      defaultVoiceLanguage: AppForms.yup.string().label().nullable(),
      voiceBackgroundNoise: AppForms.yup.string().label().nullable(),
      voiceInstructions: AppForms.yup.string().label().max(4096).nullable(),
      // SIP Phone Configuration fields
      sipPhoneNumber: AppForms.yup.string().label().nullable().max(20),
      sipDispatchRuleId: AppForms.yup.string().label().nullable(),
      enableInboundSip: AppForms.yup.bool().label().nullable(),
      enableOutboundSip: AppForms.yup.bool().label().nullable(),
      agentFnParams: AppForms.yup.object().label().nullable(),
      customModerations: AppForms.yup
        .object()
        .label()
        .nullable()
        .test(
          "custom-moderations",
          "Custom Moderations must be up to 20 items with key and value as strings",
          (value) => {
            if (value && Object.keys(value) && Object.keys(value).length < 20) {
              // check if all keys are strings and all values are strings
              const keys = Object.keys(value);
              const values = Object.values(value);
              const allKeysAreStrings = keys.every(
                (key) => typeof key === "string",
              );
              const allValuesAreStrings = values.every(
                (value) => typeof value === "string",
              );
              return allKeysAreStrings && allValuesAreStrings;
            } else if (value == null) return true;
            else return false;
          },
        ),
      enableJsonOutput: AppForms.yup.bool().label().nullable(),
      jsonOutputStructure: AppForms.yup
        .object()
        .label()
        .nullable()
        .test(
          "json-output-structure-length",
          "JSON output structure cannot exceed 12000 characters",
          (value) => {
            if (value == null) return true;
            const jsonString = JSON.stringify(value);
            return jsonString.length <= 12000;
          },
        ),
      predictedPlanJson: AppForms.yup
        .mixed()
        .label()
        .nullable()
        .test(
          "predicted-plan-json-length",
          "Static Plan JSON cannot exceed 12000 characters",
          (value) => {
            if (value == null) return true;
            const jsonString = JSON.stringify(value);
            return jsonString.length <= 12000;
          },
        ),
      agentVersion: AppForms.yup
        .string()
        .label()
        .nullable()
        .max(20)
        .matches(/^\d+\.\d+\.\d+$/, "Use format 1.2.3"),
      agentCategoryType: AppForms.yup.string().label().nullable(),
    });

    const handleDeletion = async (item, configType) => {
      try {
        const roleToDelete = displayedRoles[selectedIndex];
        const deletionResult = await Agents.deleteAgent(roleToDelete?.id);
        const newAgents = state?.agents?.filter(
          (agent) => agent.id !== roleToDelete?.id,
        );
        dispatch("SET_AGENTS", newAgents);
        history.push(
          `/workspaces/${getActiveWorkspace()?.id}/settings/roles_settings`,
        );
        setShowQaRoleModal(false);
        setSelectedIndex(null);

        // change here
        // navigateBack();
        AppToasty.show(t("qaDeletionSuccessCaption"), {
          type: "success",
        });
      } catch (error) {
        console.error("", error);
        AppToasty.show(t("qaDeletionFailedCaption"), {
          type: "danger",
        });
      }
    };
    const navigateBack = () => {
      setDisplayedView(backTo);
    };

    const _view = useMemo(() => (
      <AppFadeView
        style={
          isBuilderLayout
            ? {
                flex: 1,
                width: "100%",
                minWidth: isLargeScreen() ? "100%" : 300,
                paddingHorizontal: 24,
                paddingBottom: 40,
                paddingTop: 12,
              }
            : {
                horizontalMargin: 10,
                paddingHorizontal: 20,
                minWidth: isLargeScreen() ? "100%" : 300,
                paddingBottom: 10,
                height: 500,
              }
        }
      >
        {backTo && (
          <AppMenu.DefaultBackOptionComponent
            navigateBack={navigateBack}
            sectionTitle={"Role : " + sectionTitle}
            isClickable
            isBackIconDisplayed
          />
        )}

        <AppForms.AppForm
          key={selectedId}
          initialValues={initialMapValues}
          onSubmit={handleGenerationItemSave}
          validationSchema={validationSchema}
          enableReinitialize
          ref={ref}
        >
          <QaSettingsAIView
            isChatBot={isChatBot}
            initialMapValues={initialMapValues}
            availableFunctions={availableFunctions}
            availableFunctionsLoaded={availableFunctionsLoaded}
            customCodeExecutionEnvironmentsLoaded={
              customCodeExecutionEnvironmentsLoaded
            }
            customCodeExecutionEnvironments={customCodeExecutionEnvironments}
            handleDeletion={handleDeletion}
            isEdit={isEdit}
            settingType={settingType}
            isBusinessAnalyst={isBusinessAnalyst}
            isContentCreator={isContentCreator}
            agentsChannels={agentsChannels}
            clearMemory={clearMemory}
            isAsyncProcessInProgress={isAsyncProcessInProgress}
            saveVersion={saveVersion}
            isAIGenerated={isAIGenerated}
            isBuilderLayout={isBuilderLayout}
            builderActiveSection={builderActiveSection}
            onBuilderContextChange={onBuilderContextChange}
          />
        </AppForms.AppForm>
      </AppFadeView>
    ));
    return _view;
  },
);

export default QaSettingsAI;
